/** @format */

import React, { useState, useEffect, useCallback } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import moment from "moment";
import usePermissions from "../../../permissions/permission";
import Cookies from "js-cookie";

const Leave_approve_table = () => {
  const [leaveData, setLeaveData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [requestedDate, setRequestedDate] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [department, setDepartment] = useState("");
  const [approvedStatus, setApprovedStatus] = useState("");
  const [leaveCategories, setLeaveCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const { hasPermission } = usePermissions();
  const rowsPerPage = 10;
  const token = Cookies.get("user_token");


  // ====== Styling helpers ======
  const avatarBgClass = (seed = "") => {
    const palette = [
      "bg-sky-500",
      "bg-indigo-500",
      "bg-emerald-500",
      "bg-rose-500",
      "bg-amber-500",
      "bg-teal-500",
      "bg-fuchsia-500",
      "bg-cyan-500",
    ];
    let hash = 0;
    for (let i = 0; i < seed.length; i++)
      hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
  };

  const getInitials = (fullName = "") => {
    const tokens = String(fullName)
      .replace(/[^\p{L}\p{N}\s'-]/gu, "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (tokens.length === 0) return "??";
    if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
    const first = tokens[0][0] || "";
    const last = tokens[tokens.length - 1][0] || "";
    return (first + last).toUpperCase();
  };


  const fetchData = useCallback(
    async (page = 1, keyword = search.trim()) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page,
          limit: rowsPerPage,
        });

        if (keyword) params.append("search", keyword);
        if (requestedDate) params.append("requested_date", requestedDate);
        if (categoryName) params.append("category_name", categoryName);
        if (department) params.append("department", department);
        if (approvedStatus) params.append("approved_status_1", approvedStatus);

        const res = await fetch(`${API_URL}/v1/hris/leave/getleave?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok)
          throw new Error(`Error ${res.status}: Failed to fetch data`);

        const result = await res.json();
        setLeaveData(result.data || []);
        setTotalPages(Math.ceil(result.totalItems / rowsPerPage));
        setCurrentPage(result.currentPage || 1);
      } catch (error) {
        console.error("❌ Fetch error:", error);
        setLeaveData([]);
      } finally {
        setLoading(false);
      }
    },
    [
      API_URL,
      token,
      rowsPerPage,
      requestedDate,
      categoryName,
      department,
      approvedStatus,
      search,
    ]
  );

  // 🔹 Auto fetch when typing stops
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(1, search);
    }, 600); // debounce 600ms
    return () => clearTimeout(timeout);
  }, [search, fetchData]);

  // 🔹 Load initial data
  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, requestedDate, categoryName, department, approvedStatus]);

  // 🔹 Fetch dropdown data
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [catsRes, deptRes] = await Promise.all([
          fetch(`${API_URL}/v1/hris/leave/getLeaveCategory`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_URL}/v1/hris/department`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const catsJson = await catsRes.json();
        const deptJson = await deptRes.json();

        setLeaveCategories(catsJson?.data?.map((c) => c.category_name) || []);
        setDepartments([
          "All Departments",
          ...(deptJson?.data?.map((d) => d.name) || []),
        ]);
      } catch (err) {
        console.error("Dropdown fetch error:", err);
      }
    };
    fetchDropdowns();
  }, [API_URL, token]);

  const handleDepartmentSelect = (selectedDept) => {
    setDepartment(selectedDept === "All Departments" ? "" : selectedDept);
    setIsDropdownOpen(false);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "NOT APPROVED":
        return "bg-yellow-100 text-yellow-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // 🔹 Export CSV
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (requestedDate) params.append("requested_date", requestedDate);
      if (categoryName) params.append("category_name", categoryName);
      if (department) params.append("department", department);
      if (approvedStatus) params.append("approved_status_1", approvedStatus);

      const res = await fetch(`${API_URL}/v1/hris/leave/getleave?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch data for export");

      const result = await res.json();
      const allData = result.data || [];
      if (allData.length === 0) return alert("No data to export.");

      const csvData = allData.map((leave) => ({
        ID: leave.employee_no,
        "Employee Name": leave.employee_fullname,
        Department: leave.department,
        "Applied Date": moment(leave.requesting_date).format("YYYY-MM-DD"),
        Category: leave.category_name,
        "Requested Date": moment(leave.requested_date).format("YYYY-MM-DD"),
        Reason: leave.reason,
        Status: leave.approved_status_1,
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "leave_approve_data.csv");
    } catch (err) {
      console.error("CSV Export Error:", err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-4 font-montserrat">

      {/* 🔹 Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4 p-4 border rounded-lg shadow-sm">
        {/* Universal Search */}
        <input
          className="border border-gray-300 rounded-lg p-2"
          placeholder="Search by Name, ID, Email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <input
          type="date"
          className="border border-gray-300 rounded-lg p-2"
          value={requestedDate}
          onChange={(e) => setRequestedDate(e.target.value)}
        />

   

        <select
          value={approvedStatus}
          onChange={(e) => setApprovedStatus(e.target.value)}
          className="border border-gray-300 rounded-lg p-2"
        >
          <option value="">All Status</option>
          <option value="APPROVED">Approved</option>
          <option value="NOT APPROVED">Not Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      {/* 🔹 Export */}
      <div className="flex justify-end mb-3">
        {hasPermission(3410) && (
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-600 disabled:opacity-50"
            onClick={exportToCSV}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Export CSV"}
          </button>
        )}
      </div>

      {/* 🔹 Table */}
      <div className="overflow-x-auto bg-white shadow p-2 rounded-xl">
        <table className="w-full border-collapse text-left text-sm text-gray-600">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-3">ID</th>
              <th className="px-3 py-3 ">Employee</th>
              <th className="px-3 py-3">Department</th>
              <th className="px-3 py-3">Applied</th>
              <th className="px-3 py-3">Category</th>
              <th className="px-3 py-3">Requested Date</th>
              <th className="px-3 py-3">Reason</th>
              <th className="px-3 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : leaveData.length > 0 ? (
              leaveData.map((leave) => (
                <tr key={leave.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3">{leave.employee_no}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      {/* Avatar circle */}
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold ${avatarBgClass(
                          leave.employee_fullname || leave.employee_no
                        )}`}
                        title={leave.employee_fullname}
                      >
                        {getInitials(leave.employee_fullname || leave.employee_no)}
                      </div>

                      {/* Name + ID */}
                      <div>
                        <div className="font-semibold leading-5">
                          {leave.employee_fullname || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {leave.employee_no ? `EMP ID: ${leave.employee_no}` : ""}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-3">{leave.department || "N/A"}</td>
                  <td className="px-3 py-3">
                    {moment(leave.requesting_date).format("YYYY-MM-DD")}
                  </td>
                  <td className="px-3 py-3">{leave.category_name || "N/A"}</td>
                  <td className="px-3 py-3">
                    {moment(leave.requested_date).format("YYYY-MM-DD")}
                  </td>
                  <td className="px-3 py-3">{leave.reason || "N/A"}</td>
                  <td className="px-3 py-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(
                        leave.approved_status_1
                      )}`}
                    >
                      {leave.approved_status_1 || "PENDING"}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4 text-gray-500">
                  No data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 🔹 Pagination */}
      <div className="flex justify-center items-center mt-4 space-x-3">
        <button
          onClick={() => setCurrentPage((p) => p - 1)}
          disabled={currentPage === 1 || loading}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span>
          Page {currentPage} / {totalPages || 1}
        </span>
        <button
          onClick={() => setCurrentPage((p) => p + 1)}
          disabled={currentPage === totalPages || loading}
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Leave_approve_table;
